/**
 * useNarrationJobStatus - Hook for tracking narration script generation status
 *
 * Polls for active narration jobs and reconnects SSE when an active job is detected.
 * This ensures that narration job subscriptions persist across navigation and page refresh.
 *
 * Pattern: Similar to useOutlineSlideStatus for slide generation tracking.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useVideoWorkspaceStore } from '@/stores/videoWorkspaceStore';
import { conversationAPI } from '@/services/api';
import { realtimeStreamManager } from '@/services/realtimeStreamManager';

interface UseNarrationJobStatusOptions {
  /** Poll interval in milliseconds (default: 3000ms) */
  pollInterval?: number;
  /** Whether polling is enabled (default: true) */
  enabled?: boolean;
}

export function useNarrationJobStatus(
  conversationId: string | null,
  options: UseNarrationJobStatusOptions = {}
) {
  const { pollInterval = 3000, enabled = true } = options;

  // Get narration job state from store (persists across navigation)
  const narrationJobState = useVideoWorkspaceStore(
    (state) => conversationId
      ? state.narrationJobStateByConversation[conversationId] ?? null
      : null
  );

  const setNarrationJobState = useVideoWorkspaceStore(
    (state) => state.setNarrationJobState
  );
  const clearNarrationJobState = useVideoWorkspaceStore(
    (state) => state.clearNarrationJobState
  );

  // Track if there's currently an active generation
  const isGenerating = narrationJobState?.isGenerating ?? false;

  // Ref to track the last fetch time for debouncing
  const lastFetchRef = useRef<number>(0);

  // Fetch active narration jobs from API
  const fetchJobStatus = useCallback(async () => {
    if (!conversationId) return;

    // Debounce: don't fetch if less than 1 second since last fetch
    const now = Date.now();
    if (now - lastFetchRef.current < 1000) {
      return;
    }
    lastFetchRef.current = now;

    try {
      // Query for active jobs with narration context
      const response = await conversationAPI.getActiveMessageJobs(conversationId, {
        contextSource: 'narration'
      });

      if (response.hasActiveJob && response.activeMessageId) {
        console.log('[useNarrationJobStatus] Found active narration job:', {
          messageId: response.activeMessageId,
          conversationId,
        });

        // Update store state
        setNarrationJobState(conversationId, {
          isGenerating: true,
          activeMessageId: response.activeMessageId,
          activeJobId: response.jobs[0]?.jobId || null,
        });

        // Reconnect SSE stream if not already connected
        // This restores the real-time updates after navigation/refresh
        realtimeStreamManager.startStream(
          response.activeMessageId,
          conversationId,
          0 // Start from beginning since we don't track chunk index
        );
      } else if (isGenerating) {
        // No active job found but we thought we were generating
        // This means the job completed while we were away
        console.log('[useNarrationJobStatus] No active job found, clearing generating state');
        clearNarrationJobState(conversationId);
      }
    } catch (error) {
      console.error('[useNarrationJobStatus] Failed to fetch job status:', error);
    }
  }, [conversationId, isGenerating, setNarrationJobState, clearNarrationJobState]);

  // Initial fetch on mount and when conversationId changes
  useEffect(() => {
    if (!enabled || !conversationId) return;

    // Check store first (instant UI), then verify with API
    fetchJobStatus();
  }, [conversationId, enabled, fetchJobStatus]);

  // Poll for updates when generating
  useEffect(() => {
    if (!enabled || !conversationId || !isGenerating) return;

    console.log('[useNarrationJobStatus] Starting poll for conversation:', conversationId);

    const intervalId = setInterval(fetchJobStatus, pollInterval);

    return () => {
      console.log('[useNarrationJobStatus] Stopping poll');
      clearInterval(intervalId);
    };
  }, [conversationId, enabled, pollInterval, isGenerating, fetchJobStatus]);

  // Listen for scripts-batch-updated event to clear state
  useEffect(() => {
    if (!conversationId) return;

    const handleScriptsBatchUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { conversationId: eventConvId } = customEvent.detail || {};

      // Only clear if the event is for our conversation
      if (!eventConvId || eventConvId === conversationId) {
        console.log('[useNarrationJobStatus] scripts-batch-updated received, clearing state');
        clearNarrationJobState(conversationId);
      }
    };

    window.addEventListener('scripts-batch-updated', handleScriptsBatchUpdated);
    return () => {
      window.removeEventListener('scripts-batch-updated', handleScriptsBatchUpdated);
    };
  }, [conversationId, clearNarrationJobState]);

  // Handle visibility change (tab switch back)
  useEffect(() => {
    if (!enabled || !conversationId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isGenerating) {
        console.log('[useNarrationJobStatus] Tab became visible, refetching status');
        fetchJobStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [conversationId, enabled, isGenerating, fetchJobStatus]);

  return {
    isGenerating,
    activeMessageId: narrationJobState?.activeMessageId ?? null,
    activeJobId: narrationJobState?.activeJobId ?? null,
    generatingThinking: narrationJobState?.generatingThinking,
    refetch: fetchJobStatus,
    setGenerating: useCallback((generating: boolean, messageId?: string, jobId?: string) => {
      if (!conversationId) return;
      if (generating) {
        setNarrationJobState(conversationId, {
          isGenerating: true,
          activeMessageId: messageId ?? null,
          activeJobId: jobId ?? null,
        });
      } else {
        clearNarrationJobState(conversationId);
      }
    }, [conversationId, setNarrationJobState, clearNarrationJobState]),
  };
}

export default useNarrationJobStatus;
